// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-UI-2 — values of the pharmacy-defined columns (custom, Drug Reference) for EVERY row of a
// browser-held Rx table, loaded only when somebody opens a funnel on one of those columns.
//
// The tables resolve these values for the page on screen, which is right for display: a patient
// with 900 prescriptions should not cost 900 lookups to show 25 rows. But a funnel filters the
// whole set, and a row whose value was never loaded would silently fail every filter. So the
// full set is fetched once, on demand, through the same chunked batch calls the page uses.
import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../api/client'

export function useRxSideValuesAll(allIds: string[], want: { custom: boolean; drugref: boolean }) {
  const [cfv, setCfv] = useState<Record<string, Record<string, string | null>>>({})
  const [dr, setDr] = useState<Record<string, api.RxDrugRefFields>>({})
  const [state, setState] = useState<'idle' | 'loading' | 'ready'>('idle')
  const key = allIds.join(',')
  const live = useRef(key)
  live.current = key

  useEffect(() => { setState('idle'); setCfv({}); setDr({}) }, [key])

  const load = useCallback(() => {
    if (state !== 'idle' || allIds.length === 0 || (!want.custom && !want.drugref)) return
    const started = key
    setState('loading')
    Promise.all([
      want.custom
        ? api.batchCustomFieldValues('rx', allIds).then(r => r.values ?? {})
        : Promise.resolve({} as Record<string, Record<string, string | null>>),
      want.drugref
        ? api.batchDrugRefForRx(allIds).then(r => {
            // Server key casing must never matter to the lookup.
            const norm: Record<string, api.RxDrugRefFields> = {}
            for (const [k, v] of Object.entries(r.values ?? {})) norm[k.toLowerCase()] = v
            return norm
          })
        : Promise.resolve({} as Record<string, api.RxDrugRefFields>),
    ]).then(([c, d]) => {
      if (live.current !== started) return   // the row set changed while this was in flight
      setCfv(c); setDr(d); setState('ready')
    }).catch(() => { if (live.current === started) setState('idle') })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, key, want.custom, want.drugref])

  return { cfv, dr, load, ready: state === 'ready', loading: state === 'loading' }
}
